import axios from 'axios';
import { reactive } from 'vue';
import { getPartnerToken } from '@/services/auth/partnerSessionStorage';

// PIN de empleados para acciones críticas en la cuenta gestor (compartida en el TPV).
// El servidor decide qué es crítico: responde 428 (staff_pin_required) antes de
// ejecutar nada. Aquí se pide el PIN, se obtiene un token de actor de corta
// duración y se repite la misma petición con la cabecera X-EWU-Staff-Actor.
// El servidor vuelve a comprobar los permisos de ese empleado en cada petición.
export const ACTOR_HEADER = 'X-EWU-Staff-Actor';
// Las peticiones seguidas de una misma acción (cancelar varias comandas, un
// cobro repartido…) reutilizan el PIN unos segundos; después se vuelve a pedir.
const REUSE_WINDOW_MS = 5000;
const MAX_PIN_ATTEMPTS = 3;
const NOTICE_MS = 3500;
const BASE = '/v1/api/partner/staff/actions';

const apiUrl = () => process.env.VUE_APP_API_URL || '';
// Cliente propio para el PIN: sin interceptores globales (un PIN incorrecto no es una sesión caducada).
const client = axios.create({ baseURL: process.env.VUE_APP_API_URL, timeout: 10000 });
const authHeaders = () => ({ Authorization: `Bearer ${getPartnerToken()}` });

export const staffPinPrompt = reactive({
  open: false,
  action: '',
  actionLabel: '',
  error: '',
});
export const staffActorNotice = reactive({ visible: false, name: '' });

let pendingPrompt = null;
let resolvePrompt = null;
let activeActor = null;
let noticeTimer = null;
let interceptorInstalled = false;

// --- Ventana de PIN -------------------------------------------------------------

export function requestStaffPin({ action, actionLabel, error = '' }) {
  // Varias peticiones de la misma acción a la vez comparten una sola ventana.
  if (pendingPrompt) return pendingPrompt;
  pendingPrompt = new Promise((resolve) => {
    resolvePrompt = resolve;
    Object.assign(staffPinPrompt, { open: true, action, actionLabel: actionLabel || 'esta acción', error });
  }).finally(() => {
    pendingPrompt = null;
    resolvePrompt = null;
  });
  return pendingPrompt;
}

export function completeStaffPin(result) {
  Object.assign(staffPinPrompt, { open: false, error: '' });
  if (result?.token) activateActor(result);
  resolvePrompt?.(result?.token || null);
}

function activateActor({ token, actor, scoped }) {
  // Un token de consulta (ver el registro) solo vale para esa consulta: no se reutiliza en otras.
  if (!scoped) activeActor = { token, session: getPartnerToken(), expiresAt: Date.now() + REUSE_WINDOW_MS };
  staffActorNotice.name = actor?.name || '';
  staffActorNotice.visible = Boolean(staffActorNotice.name);
  if (noticeTimer) window.clearTimeout(noticeTimer);
  noticeTimer = window.setTimeout(() => { staffActorNotice.visible = false; }, NOTICE_MS);
}

function currentActorToken() {
  if (!activeActor) return null;
  if (Date.now() > activeActor.expiresAt || activeActor.session !== getPartnerToken()) {
    activeActor = null;
    return null;
  }
  return activeActor.token;
}

export function clearStaffActor() {
  activeActor = null;
}

// --- Interceptor ----------------------------------------------------------------

function isCloudApiRequest(config) {
  const base = apiUrl();
  return Boolean(base && String(config?.url || '').startsWith(base));
}

function setHeader(config, name, value) {
  if (typeof config.headers?.set === 'function') config.headers.set(name, value);
  else config.headers = { ...(config.headers || {}), [name]: value };
}

function cancelledError(error, data) {
  const message = `Acción cancelada: falta el PIN para «${data.action_label || 'esta acción'}».`;
  error.message = message;
  error.code = 'STAFF_PIN_CANCELLED';
  error.ewuStaffPinCancelled = true;
  if (error.response) error.response.data = { ...data, error: message, message, detail: message };
  return error;
}

export function isStaffPinCancelled(error) {
  return Boolean(error?.ewuStaffPinCancelled);
}

export function installStaffPinInterceptor() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  axios.interceptors.request.use((config) => {
    if (!isCloudApiRequest(config) || config.__ewuStaffActorToken) return config;
    const token = currentActorToken();
    if (token) setHeader(config, ACTOR_HEADER, token);
    return config;
  });

  axios.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    const data = error?.response?.data || {};
    const status = Number(error?.response?.status);
    const pinRequired = status === 428 && data.code === 'staff_pin_required';
    const pinForbidden = status === 403 && data.code === 'staff_pin_forbidden';
    if (!config || !isCloudApiRequest(config) || !(pinRequired || pinForbidden)) throw error;

    // El servidor rechazó la petición antes de ejecutarla: repetirla es seguro.
    if (pinForbidden) clearStaffActor();
    const attempts = (config.__ewuStaffPinAttempts || 0) + 1;
    if (attempts > MAX_PIN_ATTEMPTS) throw error;
    const token = await requestStaffPin({
      action: data.action,
      actionLabel: data.action_label,
      error: pinForbidden ? data.error : '',
    });
    if (!token) throw cancelledError(error, data);
    config.__ewuStaffPinAttempts = attempts;
    config.__ewuStaffActorToken = token;
    setHeader(config, ACTOR_HEADER, token);
    return axios.request(config);
  });
}

// --- API ------------------------------------------------------------------------

export async function fetchPinActors(action) {
  const { data } = await client.get(`${BASE}/actors`, {
    headers: authHeaders(),
    params: { action },
  });
  return data.users || [];
}

export async function verifyStaffPin({ userId, pin, action }) {
  const { data } = await client.post(`${BASE}/pin`, {
    user_id: userId,
    pin,
    action,
  }, { headers: authHeaders() });
  return { token: data.actor_token, actor: data.actor, scoped: Boolean(data.scoped) };
}

export async function fetchStaffActionSettings() {
  const { data } = await axios.get(`${apiUrl()}${BASE}/settings`, { headers: authHeaders() });
  return data;
}

// Cambiar la configuración y ver el registro son acciones protegidas: van por
// el axios global para que, si el PIN está activo, se pida antes de seguir.
export async function saveStaffActionSettings(payload) {
  const { data } = await axios.put(`${apiUrl()}${BASE}/settings`, payload, { headers: authHeaders() });
  return data;
}

// La ventana del registro guarda el token del PIN y lo reutiliza en cada filtro mientras está abierta.
export async function fetchStaffActionLog(params, actorToken = null) {
  const headers = { ...authHeaders(), ...(actorToken ? { [ACTOR_HEADER]: actorToken } : {}) };
  const response = await axios.get(`${apiUrl()}${BASE}/log`, {
    headers,
    params,
    ...(actorToken ? { __ewuStaffActorToken: actorToken } : {}),
  });
  return { data: response.data, actorToken: response.config?.__ewuStaffActorToken || null };
}

export function staffActionError(error, fallback = 'No se pudo completar la operación.') {
  const data = error?.response?.data;
  return data?.error || data?.message || (typeof data?.detail === 'string' ? data.detail : '') || fallback;
}
